import { useTranslation } from 'react-i18next'

import type { LearnArticleBodyProps } from '@/features/aprender/articles'
import { LearnArticleLink, LearnSeriesNav } from '@/features/aprender/learnArticleShared'
import { cn } from '@/lib/utils'
import { LocaleLink } from '@/navigation/LocaleLink'
import { article as articleUi } from '@/shared/lib/articleStyles'
import { ArticleByline } from '@/shared/ui/ArticleByline'
import { ArticleCoverHero } from '@/shared/ui/ArticleCoverHero'
import { ArticleReadingLayout } from '@/shared/ui/ArticleReadingLayout'
import { ArticleToc } from '@/shared/ui/ArticleToc'
import '@/styles/food-cost-fonts.css'

const k = (field: string) => `learn.articles.cada-cuanto-revisar-numeros-restaurante.${field}`

const SOURCE_HREF =
    'https://www.hosteltur.com/173356_la-rentabilidad-de-la-restauracion-cae-un-09-en-un-entorno-de-mas-costes-y-regulaciones.html'

export function RevisarNumerosArticle({ article }: LearnArticleBodyProps) {
    const { t } = useTranslation()

    const tocItems = [
        { id: 'mienten', label: t(k('toc.1'), '1. Facturación y banco: los dos indicadores que más mienten') },
        { id: 'cadencia', label: t(k('toc.2'), '2. ¿Cada cuánto hay que revisar los números?') },
        { id: 'margen', label: t(k('toc.3'), '3. El margen ya viene apretado') },
        { id: 'cuenta-vieja', label: t(k('toc.4'), '4. De 2€ menos por plato a 4.800€ al año') },
        { id: 'ejemplo', label: t(k('toc.5'), '5. Un ejemplo con números encima de la mesa') },
        { id: 'matices', label: t(k('toc.6'), '6. El margen sano y la cadencia perfecta no existen') },
        { id: 'esta-semana', label: t(k('toc.7'), '7. Cinco acciones para esta semana: food cost, IVA y PyG') },
    ]

    const weekItems = [
        t(k('week.1'), 'Saca el food cost real de tus tres platos más vendidos, empieza por el que más rota.'),
        t(
            k('week.2'),
            'Compara el food cost de este mes con el del mes pasado. Si ha subido, busca por qué antes de tocar la carta.',
        ),
        t(
            k('week.3'),
            'Aparta el IVA y la provisión de impuestos de la cuenta operativa. Lo que quede es lo que de verdad puedes gastar.',
        ),
        t(
            k('week.4'),
            'Fija un hueco fijo en tu semana, 15 minutos, un día concreto, solo para mirar ventas y caja. Que no dependa de que te acuerdes.',
        ),
        t(
            k('week.5'),
            'Bloquea una hora al mes para el PyG completo. Si no sabes por dónde empezar, esa es la primera señal de que necesitas ayuda para montarlo.',
        ),
    ]

    return (
        <div className={articleUi.page}>
            <article className={articleUi.shell}>
                <ArticleCoverHero
                    category={article.category}
                    title={article.title}
                    dek={
                        <>
                            {t(
                                k('dek'),
                                'Ni a diario ni una vez al trimestre: cadencia semanal, mensual y trimestral para no enterarte del problema con dos meses de retraso.',
                            )}
                        </>
                    }
                    imageSrc={article.imagePath}
                    imageAlt={t(k('heroAlt'), 'Barra de un bar por la mañana, con un café y una libreta cerrada')}
                    byline={
                        <ArticleByline
                            authorName={article.authorName}
                            readingMinutes={article.readingMinutes}
                            bylineBy={t(k('bylineBy'), 'Por')}
                        />
                    }
                />
                <ArticleReadingLayout
                    intro={
                        <div className={articleUi.proseIntro}>
                            <p className={cn(articleUi.p, articleUi.pGapMd)}>
                                <span className={articleUi.em}>
                                    {t(
                                        k('lead'),
                                        'La rentabilidad media de un restaurante en España se mueve, según la fuente que consultes, entre el 5% y el 15%. En 2025 la restauración perdió un 0,9% de margen por la subida de costes, según el Anuario de Hostelería de España. Revisar los números solo a final de trimestre significa enterarte del problema con dos o tres meses de retraso. Y con márgenes así de ajustados, ese retraso es el que convierte un mal mes en un año perdido.',
                                    )}
                                </span>
                            </p>
                            <p className={cn(articleUi.p, articleUi.pGapMd)}>
                                {t(k('intro.1'), 'Muchos hosteleros hacen lo que pueden. Ahí me incluyo yo.')}
                            </p>
                            <p className={cn(articleUi.p, articleUi.pGapMd)}>
                                {t(
                                    k('intro.2'),
                                    'Acabamos posponiendo o delegando el control de los números porque el día a día no deja hueco para más.',
                                )}
                            </p>
                            <p className={cn(articleUi.p, articleUi.pGapMd)}>
                                {t(
                                    k('intro.3'),
                                    'Miramos la cuenta general a final de trimestre, de temporada o incluso de año. Mientras tanto, dos meses que se tuercen nos revientan el ejercicio entero sin que nos demos ni cuenta.',
                                )}
                            </p>
                            <p className={cn(articleUi.p, articleUi.pGapMd)}>
                                {t(
                                    k('intro.4'),
                                    'La facturación la miramos todos. La cuenta del banco, también. El problema es que las dos mienten: la facturación no dice cuánto beneficio te deja realmente, y el banco no te cuenta cuánto de ese dinero ya le debes a Hacienda.',
                                )}
                            </p>
                            <p className={cn(articleUi.p, articleUi.pGap2xl)}>
                                {t(
                                    k('intro.5'),
                                    'Yo fui cocinero antes que empresario. Y acabé más quemado que el aceite de un churrero, no por la cocina, sino por pensar durante años que lo gastronómico pesaba más que lo empresarial.',
                                )}
                            </p>
                        </div>
                    }
                    toc={
                        <ArticleToc
                            items={tocItems}
                            label={t(k('tocLabel'), 'En este artículo')}
                            ariaLabel={t(k('tocAria'), 'Índice de contenidos')}
                        />
                    }
                >
                    <h2 id='mienten' className={articleUi.h2}>
                        {t(k('h2.mienten'), 'Facturación y banco: los dos indicadores que más mienten')}
                    </h2>
                    <div className={articleUi.prose}>
                        <p className={cn(articleUi.p, articleUi.pGapMd)}>
                            {t(
                                k('p.mienten.1'),
                                'Antes de hablar de cada cuánto revisar, merece la pena pararse en por qué facturar mucho no es lo mismo que ganar dinero.',
                            )}
                        </p>
                        <p className={cn(articleUi.p, articleUi.pGapMd)}>
                            {t(
                                k('p.mienten.2'),
                                'La facturación suma lo que entra, no lo que queda. Un mes de ventas récord puede esconder un food cost disparado, una merma que nadie está midiendo o un plato estrella que ya no deja margen.',
                            )}
                        </p>
                        <p className={cn(articleUi.p, articleUi.pGapMd)}>
                            {t(
                                k('p.mienten.3'),
                                'El banco tampoco ayuda. Ese saldo incluye el IVA que tendrás que liquidar y, muchas veces, la provisión de impuestos que todavía no has apartado. Ver dinero en la cuenta no es lo mismo que tener beneficio.',
                            )}
                        </p>
                        <p className={cn(articleUi.p, articleUi.pGapMd)}>
                            {t(
                                k('p.mienten.4'),
                                'Por eso hace falta un tercer número: la cuenta de explotación, el PyG. Es el único que te dice, mes a mes, si el negocio gana dinero de verdad.',
                            )}
                        </p>
                        <p className={cn(articleUi.p, articleUi.pGap2xl)}>
                            {t(
                                k('p.mienten.5Before'),
                                'Ya hablamos de esta brecha entre facturar mucho y ganar poco en un artículo anterior, ',
                            )}
                            <LearnArticleLink slug='como-calcular-coste-de-un-plato'>
                                {t(k('linkCostePlato'), 'cómo calcular el coste de un plato')}
                            </LearnArticleLink>
                            {t(k('p.mienten.5After'), '.')}
                        </p>
                    </div>

                    <h2 id='cadencia' className={cn(articleUi.h2, articleUi.h2Spaced)}>
                        {t(k('h2.cadencia'), '¿Cada cuánto hay que revisar los números de un restaurante?')}
                    </h2>
                    <div className={articleUi.prose}>
                        <p className={cn(articleUi.p, articleUi.pGapMd)}>
                            {t(
                                k('p.cadencia.1'),
                                'No hay una cadencia única que sirva para todos los negocios. Pero sí hay una lógica que funciona en la mayoría:',
                            )}
                        </p>
                    </div>

                    <div className={articleUi.tableWrap}>
                        <table className={articleUi.tableFacts}>
                            <caption className='sr-only'>
                                {t(k('table.caption'), 'Cadencia de revisión y qué permite pillar a tiempo')}
                            </caption>
                            <tbody>
                                <tr>
                                    <th scope='row'>{t(k('table.row.1.cadencia'), 'Semanal (10-15 min)')}</th>
                                    <td>{t(k('table.row.1.permite'), 'Ventas del día, caja, mermas evidentes')}</td>
                                </tr>
                                <tr>
                                    <th scope='row'>{t(k('table.row.2.cadencia'), 'Mensual (1 hora)')}</th>
                                    <td>
                                        {t(
                                            k('table.row.2.permite'),
                                            'Food cost real, PyG completo, comparación con el mes anterior',
                                        )}
                                    </td>
                                </tr>
                                <tr>
                                    <th scope='row'>{t(k('table.row.3.cadencia'), 'Trimestral')}</th>
                                    <td>{t(k('table.row.3.permite'), 'Revisión de carta, precios y proveedores')}</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>

                    <div className={articleUi.prose}>
                        <p className={cn(articleUi.p, articleUi.pGapMd)}>
                            {t(
                                k('p.cadencia.2'),
                                'Controlarlo al dedillo tú solo, sin ayuda, es prácticamente imposible. No es una opinión, es aritmética: llevar sala, cocina y turnos ya te ocupa el día entero.',
                            )}
                        </p>
                        <p className={cn(articleUi.p, articleUi.pGapMd)}>
                            {t(
                                k('p.cadencia.3'),
                                'Y verlo tampoco es lo mismo que solucionarlo. Puedes tener el PyG delante y no saber qué palanca tocar primero.',
                            )}
                        </p>
                        <p className={cn(articleUi.p, articleUi.pGap2xl)}>
                            <span className={articleUi.em}>
                                {t(
                                    k('p.cadencia.4'),
                                    'Por eso lo mensual importa más que lo semanal: es la cadencia mínima para detectar una desviación antes de que se coma un trimestre entero.',
                                )}
                            </span>
                        </p>
                    </div>

                    <h2 id='margen' className={cn(articleUi.h2, articleUi.h2Spaced)}>
                        {t(k('h2.margen'), 'El margen ya viene apretado')}
                    </h2>
                    <div className={articleUi.prose}>
                        <p className={cn(articleUi.p, articleUi.pGapMd)}>
                            {t(k('p.margen.1'), 'Aquí toca la parte que no vende tan bien.')}
                        </p>
                        <p className={cn(articleUi.p, articleUi.pGapMd)}>
                            <span className={articleUi.em}>
                                {t(
                                    k('p.margen.2'),
                                    'En 2025, la restauración española perdió un 0,9% de rentabilidad, mientras el alojamiento la mejoraba un 2,1%, según el Anuario de Hostelería de España. La patronal del sector atribuye la caída al alza de costes y a la presión regulatoria.',
                                )}
                            </span>
                        </p>
                        <p className={cn(articleUi.p, articleUi.pGapMd)}>
                            {t(
                                k('p.margen.3'),
                                'Esto significa que el margen de partida ya es estrecho para buena parte del sector. Y un margen estrecho no perdona esperar.',
                            )}
                        </p>
                        <p className={cn(articleUi.p, articleUi.pGapMd)}>
                            {t(k('p.margen.4Before'), 'Si tu plato estrella, el que vendes, por ejemplo, ')}
                            <strong>{t(k('p.margen.4Strong1'), '200 veces al mes')}</strong>
                            {t(k('p.margen.4Mid'), ', empieza a dejarte ')}
                            <strong>{t(k('p.margen.4Strong2'), '2€ menos')}</strong>
                            {t(k('p.margen.4Mid2'), ' por unidad, son ')}
                            <strong>{t(k('p.margen.4Strong3'), '400€ al mes')}</strong>
                            {t(
                                k('p.margen.4After'),
                                ' que desaparecen sin figurar en ningún sitio como "problema". Solo como una cifra algo más baja de lo esperado.',
                            )}
                        </p>
                        <p className={cn(articleUi.p, articleUi.pGap2xl)}>
                            {t(
                                k('p.margen.5'),
                                'La pregunta real no es si te vas a enterar. Es cuánto tiempo estás dispuesto a esperar antes de arreglarlo: ¿200€ perdidos? ¿400€? ¿los doce meses del año?',
                            )}
                        </p>
                    </div>

                    <h2 id='cuenta-vieja' className={cn(articleUi.h2, articleUi.h2Spaced)}>
                        {t(k('h2.cuentaVieja'), 'De 2€ menos por plato a 4.800€ al año')}
                    </h2>
                    <div className={articleUi.prose}>
                        <p className={cn(articleUi.p, articleUi.pGapMd)}>
                            {t(
                                k('p.cuentaVieja.1Before'),
                                'Sigamos con el mismo plato. 200 unidades al mes, 2€ menos de margen: son ',
                            )}
                            <strong>{t(k('p.cuentaVieja.1Strong'), '400€ al mes')}</strong>
                            {t(k('p.cuentaVieja.1After'), '.')}
                        </p>
                        <p className={cn(articleUi.p, articleUi.pGapMd)}>
                            {t(k('p.cuentaVieja.2Before'), 'En un trimestre, ')}
                            <strong>{t(k('p.cuentaVieja.2Strong1'), '1.200€')}</strong>
                            {t(k('p.cuentaVieja.2Mid'), '. En un año, ')}
                            <strong>{t(k('p.cuentaVieja.2Strong2'), '4.800€')}</strong>
                            {t(k('p.cuentaVieja.2After'), ', de un solo plato, sin que nadie se haya dado cuenta.')}
                        </p>
                        <p className={cn(articleUi.p, articleUi.pGapMd)}>
                            {t(
                                k('p.cuentaVieja.3'),
                                'Multiplica eso por los tres o cuatro platos de tu carta que probablemente estén en la misma situación sin que lo sepas.',
                            )}
                        </p>
                        <p className={cn(articleUi.p, articleUi.pGap2xl)}>
                            {t(
                                k('p.cuentaVieja.4'),
                                'Esa es la aritmética: pequeña por unidad, brutal cuando se acumula doce meses sin revisión.',
                            )}
                        </p>
                    </div>

                    <h2 id='ejemplo' className={cn(articleUi.h2, articleUi.h2Spaced)}>
                        {t(k('h2.ejemplo'), 'Un ejemplo con números encima de la mesa')}
                    </h2>
                    <div className={articleUi.prose}>
                        <p className={cn(articleUi.p, articleUi.pGapMd)}>
                            {t(k('p.ejemplo.1Before'), 'Imagina un restaurante que factura ')}
                            <strong>{t(k('p.ejemplo.1Strong'), '35.000€ al mes')}</strong>
                            {t(k('p.ejemplo.1After'), ', cifra ilustrativa, no un caso real.')}
                        </p>
                        <p className={cn(articleUi.p, articleUi.pGapMd)}>
                            {t(k('p.ejemplo.2Before'), 'Con un margen neto saludable del 8%, debería dejar unos ')}
                            <strong>{t(k('p.ejemplo.2Strong'), '2.800€')}</strong>
                            {t(k('p.ejemplo.2After'), ' de beneficio mensual.')}
                        </p>
                        <p className={cn(articleUi.p, articleUi.pGapMd)}>
                            {t(
                                k('p.ejemplo.3Before'),
                                'Si nadie revisa el PyG durante un trimestre y el food cost sube dos puntos sin que se note en caja, ese margen puede caer a la mitad: de ',
                            )}
                            <strong>{t(k('p.ejemplo.3Strong'), '2.800€ a 1.400€')}</strong>
                            {t(k('p.ejemplo.3After'), ' al mes.')}
                        </p>
                        <p className={cn(articleUi.p, articleUi.pGapMd)}>
                            {t(k('p.ejemplo.4Before'), 'En tres meses, son ')}
                            <strong>{t(k('p.ejemplo.4Strong'), '4.200€ de beneficio')}</strong>
                            {t(
                                k('p.ejemplo.4After'),
                                ' que no van a aparecer en ningún sitio como "hemos perdido dinero". Solo como un trimestre flojo.',
                            )}
                        </p>
                        <p className={cn(articleUi.p, articleUi.pGap2xl)}>
                            {t(
                                k('p.ejemplo.5'),
                                'Esa es la diferencia entre llevar un restaurante y, además, tener un negocio: no está en cocinar mejor. Está en enterarte a tiempo.',
                            )}
                        </p>
                    </div>

                    <h2 id='matices' className={cn(articleUi.h2, articleUi.h2Spaced)}>
                        {t(k('h2.matices'), 'El margen sano y la cadencia perfecta no existen')}
                    </h2>
                    <div className={articleUi.prose}>
                        <p className={cn(articleUi.p, articleUi.pGapMd)}>
                            {t(
                                k('p.matices.1'),
                                'Aquí no hay letra pequeña de una normativa, pero sí hay matices que conviene dejar claros.',
                            )}
                        </p>
                        <p className={cn(articleUi.p, articleUi.pGapMd)}>
                            {t(
                                k('p.matices.2'),
                                'El margen "sano" para un restaurante varía mucho según a quién le preguntes: unas fuentes hablan del 3-5%, otras del 8-15%. No hay un número único, y conviene desconfiar de quien te lo dé sin más contexto.',
                            )}
                        </p>
                        <p className={cn(articleUi.p, articleUi.pGap2xl)}>
                            {t(
                                k('p.matices.3'),
                                'Tampoco hay una cadencia perfecta para todos los negocios. Un local con 40 cubiertos y carta corta necesita menos revisión que uno con carta amplia y varios turnos.',
                            )}
                        </p>
                    </div>

                    <h2 id='esta-semana' className={cn(articleUi.h2, articleUi.h2Spaced)}>
                        {t(k('h2.estaSemana'), 'Cinco acciones para esta semana: food cost, IVA y PyG')}
                    </h2>
                    <ol className={articleUi.numListGap2xl}>
                        {weekItems.map(item => (
                            <li key={item} className={articleUi.numItem}>
                                {item}
                            </li>
                        ))}
                    </ol>

                    <div className={articleUi.prose}>
                        <p className={cn(articleUi.p, articleUi.pGapMd)}>
                            {t(
                                k('p.estaSemana.clusterBefore'),
                                'Si todavía no tienes ni el primer termómetro —la creencia de que los números caben en la cabeza—, empieza por ',
                            )}
                            <LearnArticleLink slug='como-saber-si-mi-restaurante-es-rentable'>
                                {t(k('p.estaSemana.clusterLink'), 'cómo saber si tu restaurante es rentable')}
                            </LearnArticleLink>
                            {t(
                                k('p.estaSemana.clusterAfter'),
                                '. Este artículo es la cadencia; aquel es el punto de partida.',
                            )}
                        </p>
                        <p className={cn(articleUi.p, articleUi.pGapMd)}>
                            {t(
                                k('closing.audit'),
                                'Si sospechas que llevas meses sin saber realmente cuánto te deja tu negocio, cuéntanoslo y lo miramos.',
                            )}
                        </p>
                        <p className={cn(articleUi.p, articleUi.pGap2xl)}>
                            {t(k('closing.signoff'), 'Seguimos contando lo que a otros no les interesa contar.')}
                        </p>
                    </div>
                </ArticleReadingLayout>

                <section className={articleUi.cta}>
                    <h2 className={cn(articleUi.h2, articleUi.h2Cta)}>
                        {t(k('h2.cta'), '¿Llevas meses sin saber cuánto te deja realmente el negocio?')}
                    </h2>
                    <p className={articleUi.ctaSub}>
                        {t(
                            k('cta.sub'),
                            'Con Tipi montamos la auditoría de tus números y dejamos el PyG y el food cost claros. Cuéntanoslo y lo miramos.',
                        )}
                    </p>
                    <div className={articleUi.ctaActionsCenter}>
                        <LocaleLink to='/#form' className={cn(articleUi.btn, articleUi.btnSolid)}>
                            {t(k('cta.button'), 'Empezar con Tipi')}
                        </LocaleLink>
                    </div>
                </section>

                <LearnSeriesNav currentId={article.id} />

                <section className={articleUi.sources} aria-labelledby='fuentes-heading'>
                    <h2 id='fuentes-heading' className={articleUi.sourcesTitle}>
                        {t(k('sourcesTitle'), 'Fuentes')}
                    </h2>
                    <ul className={articleUi.sourcesList}>
                        <li>
                            <a href={SOURCE_HREF} target='_blank' rel='noopener noreferrer' className={articleUi.link}>
                                {t(
                                    k('sources.1'),
                                    'Hostelería de España, Anuario 2025, vía Hosteltur: La rentabilidad de la restauración cae un 0,9% en un entorno de más costes y regulaciones',
                                )}
                            </a>
                        </li>
                    </ul>
                </section>
            </article>
        </div>
    )
}
